/**
 * Cube component module.
 * @module shared/components/Cube
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
// import Promise from 'bluebird';
import styled, {withTheme} from 'styled-components/macro';
import {fill, layoutAttribute} from '../cssUtils';
import Row from '../Row';
import DescriptiveText from '../DescriptiveText';
import {v4 as uuid} from 'uuid';
import './Cube.css';

// ETODO
//   Default rangeEnd?
//   Drilldowns?
//   Progression?
//   Resizing/media?

// cube scripts must be loaded in this order
const CUBE_SOURCE_PATHS = [
  '/cube/three.js',
  '/cube/THREE.Text.js',
  '/cube/Detector.js',
  '/cube/resourcetracker.js',
  '/cube/cube.js',
  '/cube/cubeprogression.js',
];

function areScriptsLoaded() {
  const found = CUBE_SOURCE_PATHS.map((src, idx) => (
    document.getElementById(`cb-cube-script-${idx}`)
  )).filter(o => !!o);

  return (
    found.length === CUBE_SOURCE_PATHS.length
    && !!window.component
  );
}

function appendScriptElement({id, src, onLoad, onError}) {
  // if the sript element already exists,
  // do not attempt to create another
  if (document.getElementById(id)) {
    return;
  }

  // the script element does not exist
  // create it, append it, and wait for it to load
  const el = document.createElement('script');
  el.type = 'text/javascript';
  el.crossOrigin = true;
  el.async = false;
  el.id = id;
  el.src = src;
  el.onload = onLoad;
  el.onerror = onError;

  document.body.appendChild(el);

  return el;
}

function createCube(config) {
  const {
    cubeID,
    theme,
    leftColor,
    rightColor,
    bottomColor,
    titles,
    views,
    showTitle,
    backgroundColor = theme.tileBackgroundColor,
  } = config;

  // eslint-disable-next-line new-cap, no-undef
  const cube = new component(
    cubeID,
    backgroundColor,
    leftColor,
    bottomColor,
    rightColor,
    !showTitle
  );

  if (showTitle) {
    titles.forEach((title, idx) => {
      cube.setTitleHeader(
        idx + 1,
        title.text,
        title.color,
        title.color
      );
    });
  }

  // create views
  const viewIDs = views.map(view => {
    const viewID = cube.createView(view.text, view.color);

    // create cylinders
    view.cylinders.forEach((cylinder, idx) => {
      const {
        bottomCaptionText,
        hexColor,
        value,
        rangeStart,
        rangeEnd,
        hexColorLegendCaptionText,
      } = cylinder;

      // create cylinder returns its ID
      cube.createCylinder(
        idx + 1,
        viewID,
        bottomCaptionText,
        hexColor,
        value,
        rangeStart,
        rangeEnd,
        hexColorLegendCaptionText
      );
    });

    return viewID;
  });

  cube.setActiveView(viewIDs[0]);

  return cube;
}

function destroyCanvas(cubeID) {
  const cubeEl = document.getElementById(cubeID);
  if (cubeEl && cubeEl.firstChild) {
    cubeEl.removeChild(cubeEl.firstChild);
  }
}

const _CubeContainer = styled.div`
  height: 100%;
  max-width: 100%;
  margin: 5px;
  overflow: hidden;
  z-index: 0;
  ${layoutAttribute.css};
  ${fill()};
`;

const Cube = (props) => {
  const cubeID = React.useRef('cb-cube-' + uuid());
  const [cubeRef, setCubeRef] = React.useState(null);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  if (!isLoaded && areScriptsLoaded()) {
    setIsLoaded(true);
  }

  // cube script loading effect
  React.useLayoutEffect(() => {
    let loadedCount = 0;

    CUBE_SOURCE_PATHS.forEach((src, idx) => {
      appendScriptElement({
        id: `cb-cube-script-${idx}`,
        src,
        onLoad: () => {
          loadedCount++;
          setIsLoaded(loadedCount === CUBE_SOURCE_PATHS.length);
        },
        onError: () => {
          setHasError(true);
        },
      });
    });
  }, []);

  // cube initialization effect
  React.useEffect(() => {
    if (!isLoaded || !cubeRef) {
      return;
    }

    destroyCanvas(cubeID.current);

    createCube({
      ...props,
      cubeID: cubeID.current,
    });
  }, [isLoaded, cubeRef, props.views]);

  if (hasError) {
    return <Row>
      <DescriptiveText>
        Graph initialization failed
      </DescriptiveText>
    </Row>;
  }

  return <_CubeContainer id={cubeID.current} ref={setCubeRef} className='cb-cube' />;
};

export default withTheme(Cube);

Cube.propTypes = {
  showTitle: PropTypes.bool,
  leftColor: PropTypes.number,
  rightColor: PropTypes.number,
  bottomColor: PropTypes.number,
  backgroundColor: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  theme: PropTypes.object,

  titles: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      color: PropTypes.number,
    })
  ),

  views: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      color: PropTypes.number,

      cylinders: PropTypes.arrayOf(
        PropTypes.shape({
          bottomCaptionText: PropTypes.string,
          hexColor: PropTypes.number,
          hexColorLegendCaptionText: PropTypes.number,
          value: PropTypes.number.isRequired,
          rangeStart: PropTypes.number,
          rangeEnd: PropTypes.number,
        }).isRequired
      ).isRequired,
    })
  ),
};

Cube.defaultProps = {
  leftColor: 0x696969,
  rightColor: 0x808080,
  bottomColor: 0x000000,
  showTitle: false,
};
